<!-- src/lib/components/SharedBoardView.svelte -->
<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../api.ts';
  import type { BoardDetail } from '../types.ts';

  interface Props {
    token: string;
  }
  let { token }: Props = $props();

  let board   = $state<BoardDetail | null>(null);
  let error   = $state<string | null>(null);
  let loading = $state(true);

  const PRIORITY_COLORS: Record<string, string> = {
    urgent: 'oklch(0.62 0.26 25)',
    high:   'oklch(0.78 0.16 85)',
    medium: 'oklch(0.58 0.22 265)',
    low:    'rgba(255,255,255,0.3)',
    none:   'rgba(255,255,255,0.15)',
  };

  onMount(async () => {
    try {
      board = await api<BoardDetail>(`/api/v1/share/${token}`);
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Board not found or link expired';
    } finally {
      loading = false;
    }
  });
</script>

<div class="shared-root">
  <div class="shared-header">
    <div class="brand">
      <span class="logo-dot"></span>
      <span class="brand-name">AgentBoard</span>
    </div>
    <a href="/" class="home-link">View in AgentBoard →</a>
  </div>

  <div class="shared-body">
    {#if loading}
      <div class="loading-state">
        <div class="spinner"></div>
        <span>Loading board…</span>
      </div>
    {:else if error}
      <div class="error-state">
        <span class="error-icon">⊘</span>
        <p class="error-title">Unable to load board</p>
        <p class="error-sub">This link may have expired.</p>
        <p class="error-detail">{error}</p>
      </div>
    {:else if board}
      <div class="board-header">
        <h1 class="board-name">{board.name}</h1>
        <span class="ro-badge">Read-only</span>
      </div>

      <div class="columns-row">
        {#each board.columns as col}
          <div class="column">
            <div class="column-header">
              <span class="column-name">{col.name}</span>
              <span class="column-count">{col.cards.length}</span>
            </div>
            <div class="column-cards">
              {#each col.cards as card}
                <div class="card-item">
                  <div
                    class="card-priority-bar"
                    style="background: {PRIORITY_COLORS[card.priority] ?? PRIORITY_COLORS.none};"
                  ></div>
                  <div class="card-body">
                    <p class="card-title">{card.title}</p>
                    {#if card.description}
                      <p class="card-desc">{card.description}</p>
                    {/if}
                    <div class="card-footer">
                      <span
                        class="priority-dot"
                        style="background: {PRIORITY_COLORS[card.priority] ?? PRIORITY_COLORS.none};"
                        title={card.priority}
                      ></span>
                      <span class="priority-label">{card.priority !== 'none' ? card.priority : ''}</span>
                      {#if card.agent_id}
                        <span class="agent-chip">⊙ agent</span>
                      {/if}
                    </div>
                  </div>
                </div>
              {/each}
              {#if col.cards.length === 0}
                <div class="col-empty">No cards</div>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <footer class="shared-footer">
    <span>Shared read-only view · Powered by <span class="stencil">AgentBoard</span></span>
  </footer>
</div>

<style>
  .shared-root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: oklch(0.085 0.008 240);
    color: oklch(0.88 0.01 240);
    font-family: system-ui, -apple-system, sans-serif;
    -webkit-font-smoothing: antialiased;
  }

  /* ── Header ──────────────────────────────────────── */
  .shared-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1.75rem;
    height: 52px;
    flex-shrink: 0;
    background: rgba(255,255,255,0.02);
    border-bottom: 1px solid rgba(255,255,255,0.05);
    backdrop-filter: blur(8px);
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  .logo-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: oklch(0.80 0.26 310);
    box-shadow: 0 0 12px oklch(0.74 0.28 310 / 0.84);
  }

  .brand-name {
    font-family: 'Saira Stencil One', 'Inter', system-ui, sans-serif;
    font-size: 1.35rem;
    font-weight: 400;
    letter-spacing: 0.06em;
    color: #e8eaf0;
    text-shadow: 0 0 18px oklch(0.74 0.28 310 / 0.29);
  }

  .home-link {
    font-size: 0.8rem;
    color: oklch(0.82 0.26 310 / 0.78);
    text-decoration: none;
    border: 1px solid oklch(0.72 0.28 310 / 0.26);
    padding: 0.3rem 0.75rem;
    border-radius: 6px;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }

  .home-link:hover {
    background: oklch(0.72 0.28 310 / 0.10);
    color: oklch(0.80 0.26 310);
    border-color: oklch(0.74 0.28 310 / 0.46);
  }

  /* ── Body ────────────────────────────────────────── */
  .shared-body {
    flex: 1;
    overflow: auto;
    padding: 2rem 1.75rem;
  }

  /* ── Loading ─────────────────────────────────────── */
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 6rem;
    color: rgba(255,255,255,0.3);
    font-size: 0.875rem;
  }

  .spinner {
    width: 28px;
    height: 28px;
    border: 2px solid rgba(255,255,255,0.1);
    border-top-color: oklch(0.80 0.26 310);
    border-radius: 50%;
    animation: spin 700ms linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ── Error ───────────────────────────────────────── */
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 6rem;
    text-align: center;
  }

  .error-icon {
    font-size: 2.5rem;
    color: oklch(0.62 0.26 25 / 0.50);
  }

  .error-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: oklch(0.88 0.01 240);
    margin: 0.5rem 0 0;
  }

  .error-sub {
    font-size: 0.875rem;
    color: rgba(255,255,255,0.4);
    margin: 0;
  }

  .error-detail {
    font-size: 0.75rem;
    color: rgba(255,255,255,0.2);
    font-family: 'JetBrains Mono', monospace;
    margin: 0.5rem 0 0;
  }

  /* ── Board header ────────────────────────────────── */
  .board-header {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    margin-bottom: 1.5rem;
  }

  .board-name {
    font-size: 1.375rem;
    font-weight: 700;
    margin: 0;
    color: #e8eaf0;
  }

  .ro-badge {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.3);
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 4px;
    padding: 0.15rem 0.5rem;
  }

  /* ── Columns ─────────────────────────────────────── */
  .columns-row {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    overflow-x: auto;
    padding-bottom: 1rem;
  }

  .column {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    min-width: 260px;
    max-width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
  }

  .column-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem 0.625rem;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }

  .column-name {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: rgba(255,255,255,0.55);
  }

  .column-count {
    font-size: 0.68rem;
    color: rgba(255,255,255,0.25);
    background: rgba(255,255,255,0.05);
    border-radius: 10px;
    padding: 0.05rem 0.4rem;
    font-variant-numeric: tabular-nums;
  }

  .column-cards {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.625rem;
  }

  /* ── Card ────────────────────────────────────────── */
  .card-item {
    background: oklch(0.13 0.008 240);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 7px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .card-priority-bar {
    height: 3px;
    flex-shrink: 0;
  }

  .card-body {
    padding: 0.625rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .card-title {
    font-size: 0.83rem;
    font-weight: 600;
    color: oklch(0.88 0.01 240);
    margin: 0;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-desc {
    font-size: 0.73rem;
    color: oklch(0.55 0.01 240);
    margin: 0;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-footer {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.25rem;
    font-size: 0.65rem;
    color: rgba(255,255,255,0.25);
  }

  .priority-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .priority-label {
    text-transform: capitalize;
  }

  .agent-chip {
    color: oklch(0.76 0.26 310 / 0.52);
    font-size: 0.63rem;
  }

  .col-empty {
    font-size: 0.75rem;
    color: rgba(255,255,255,0.15);
    text-align: center;
    padding: 1rem;
  }

  /* ── Footer ──────────────────────────────────────── */
  .shared-footer {
    padding: 0.875rem 1.75rem;
    border-top: 1px solid rgba(255,255,255,0.05);
    font-size: 0.72rem;
    color: rgba(255,255,255,0.2);
    text-align: center;
    flex-shrink: 0;
  }

  .stencil {
    font-family: 'Saira Stencil One', 'Inter', system-ui, sans-serif;
    letter-spacing: 0.05em;
    color: rgba(255,255,255,0.45);
  }
</style>
